import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { TwoFactorSettings } from '../../components/security/TwoFactorSettings';
import { bannerMessage, fetchTwoFaStatus, notifyTwoFaChanged, promptFor, type TwoFaStatus } from '../../lib/twoFa';

/** Where someone goes to set up 2FA: from the notice, and the only page open once the deadline has passed. */
export function SecuritySetup() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<TwoFaStatus | null | undefined>(undefined);

  const refresh = useCallback(async () => {
    const s = await fetchTwoFaStatus();
    setStatus(s);
    // Nothing (left) to do: carry on into the app.
    if (promptFor(s) === 'none') navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    void refresh();
  }, [loading, user, navigate, refresh]);

  const overdue = status?.overdue === true;
  return (
    <div className="min-h-dvh bg-app-bg text-app-fg flex items-center justify-center px-5 py-8">
      <div className="w-full max-w-md bg-app-card border border-app-border rounded-2xl shadow-sm p-6 flex flex-col gap-4">
        <div>
          <h1 className="font-serif text-2xl text-app-fg">
            {overdue ? 'Two-factor authentication is now required' : 'Set up two-factor authentication'}
          </h1>
          <p className="text-sm text-app-muted mt-1">
            {overdue
              ? 'To keep using Sekretly, please set it up now. It takes about two minutes.'
              : status ? bannerMessage(status) : ''}
          </p>
        </div>
        <TwoFactorSettings
          onChanged={() => { notifyTwoFaChanged(); void refresh(); }}
        />
        <div className="flex items-center justify-between">
          {!overdue && (
            <button type="button" onClick={() => navigate('/', { replace: true })} className="text-xs text-app-muted hover:text-app-fg sk-hit">
              Not now
            </button>
          )}
          <button type="button" onClick={() => void signOut()} className="text-xs text-app-muted hover:text-app-fg ml-auto sk-hit">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
